import type { AssignmentConfirmationPackage } from '@local/common-lib';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { jsonDateParser } from 'json-date-parser';
import { useSnackbar } from 'notistack';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FullscreenPage } from '../../components/fullscreen-page/index.js';
import { MiddleSpinner } from '../../components/middle-spinner/index.js';
import { usePromise } from '../../hooks/use-promise.js';
import { useSetTitle } from '../../hooks/use-title.js';
import { isJwtExpired } from '../../utils/auth/util.js';
import { ErrorPage } from '../error/index.js';
import { AcceptDeclineDoneView, AcceptDeclineView } from './view.js';

export const AcceptDeclinePageEmailEntry: FC = () => {
  useSetTitle('Patrol Assignment');
  const { enqueueSnackbar } = useSnackbar();
  const { token } = useParams<{ token: string }>();
  const [busy, setBusy] = useState(false);
  const [actioned, setActioned] = useState(false);

  const [data, error] = usePromise<AssignmentConfirmationPackage>(async () => {
    const result = await axios.get<AssignmentConfirmationPackage>(
      '/api/v1/assignment',
      {
        headers: {
          authorization: `Bearer ${token ?? 'no-token'}`,
        },
        transformResponse: [
          (response: string, headers) => {
            if (
              (headers['content-type'] as string)?.includes('application/json')
            ) {
              return JSON.parse(response, jsonDateParser) as Record<
                string,
                unknown
              >;
            }
            return response;
          },
        ],
      },
    );
    return result.data;
  }, [token]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const accept = useCallback(() => {
    if (!token) {
      enqueueSnackbar('No token provided.', { variant: 'error' });
      return;
    }
    setBusy(true);
    axios
      .post<AssignmentConfirmationPackage>(
        '/api/v1/assignment/accept',
        {},
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      )
      .then(() => {
        setBusy(false);
        setActioned(true);
        enqueueSnackbar('Assignment accepted successfully.', {
          variant: 'success',
        });
        return undefined;
      })
      .catch((err) => {
        console.log(err);
        Sentry.captureException(err);
        enqueueSnackbar(String(err), { variant: 'error' });
        setBusy(false);
      });
  }, [token]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const decline = useCallback(() => {
    if (!token) {
      enqueueSnackbar('No token provided.', { variant: 'error' });
      return;
    }
    setBusy(true);
    axios
      .post<AssignmentConfirmationPackage>(
        '/api/v1/assignment/decline',
        {},
        {
          headers: {
            authorization: `Bearer ${token}`,
          },
        },
      )
      .then(() => {
        setBusy(false);
        setActioned(true);
        enqueueSnackbar('Assignment declined successfully.', {
          variant: 'success',
        });
        return undefined;
      })
      .catch((err) => {
        console.log(err);
        Sentry.captureException(err);
        enqueueSnackbar(String(err), { variant: 'error' });
        setBusy(false);
      });
  }, [token]);

  if (!token || isJwtExpired(token)) {
    return (
      <ErrorPage error={{ message: 'Sorry, your invitation has expired.' }} />
    );
  }

  if (error) return <ErrorPage error={error} />;

  if (!data) return <MiddleSpinner />;

  if (actioned) {
    return (
      <FullscreenPage>
        <AcceptDeclineDoneView me={data.me} />
      </FullscreenPage>
    );
  }

  return (
    <FullscreenPage>
      <AcceptDeclineView
        {...data}
        accept={accept}
        busy={busy}
        decline={decline}
      />
    </FullscreenPage>
  );
};
